/**
 * scheduleTaxonomy.ts
 * Frontend service for the Taxonomy API (Phase 1, Item 9).
 *
 * All requests go through apiClient (MSAL auth + error capture).
 *
 * Read endpoints (all authenticated users):
 *   GET /api/taxonomy/skills                     — active skills
 *   GET /api/taxonomy/task-types                 — active task types (?category= optional)
 *   GET /api/taxonomy/test-catalog-task-types    — TestCatalogItem → TaskType mapping
 *   GET /api/taxonomy/test-prerequisites         — active prerequisites with joined names
 *
 * Write stubs (admin only — currently return 501 from the backend):
 *   POST   /api/taxonomy/skills
 *   PUT    /api/taxonomy/skills/:key
 *   DELETE /api/taxonomy/skills/:key
 *   POST   /api/taxonomy/task-types
 *   PUT    /api/taxonomy/task-types/:key
 *   DELETE /api/taxonomy/task-types/:key
 *   POST   /api/taxonomy/test-prerequisites
 *   PUT    /api/taxonomy/test-prerequisites/:key
 *   DELETE /api/taxonomy/test-prerequisites/:key
 */

import apiClient from './apiClient';
import type { Skill, TaskType, TestPrerequisite, SkillLevel } from '../types/schedule';

// ── Extended types (with joined fields returned by the API) ──────────────────

/** TestCatalogItem → TaskType mapping row as returned by the API. */
export interface TestCatalogTaskTypeMapping {
  TestCatalogItemTaskTypeKey: string;
  TestCatalogItemKey: string;
  TaskTypeKey: string;
  TaskTypeName: string;
  TaskTypeCategory: string | null;
  LabSection: string | null;
  PresetDurationDays: number;
  DefaultMachineCount: number;
  /** Run Mode: 'continuous' types schedule in 24/7 calendar days (Phase B live). */
  RunMode: 'attended' | 'continuous';
  /** Run Mode Phase A: per-test person minutes for continuous types (null until the lab answers). */
  AttendedMinutes: number | null;
}

/** TestPrerequisite as returned by the API (includes joined names). */
export interface TestPrerequisiteDetail extends TestPrerequisite {
  PrerequisiteTestName: string;
  PrerequisiteCategory: string | null;
}

// ── Payloads for write endpoints (stubs — backend returns 501 until Phase 5) ──

export interface CreateSkillPayload {
  Name: string;
  Description?: string;
  Category?: string;
  /** Phase 4.2 / REQ-02: skill grouping Subcategory field. */
  Subcategory?: string | null;
  SortOrder?: number;
  IsActive?: boolean;
}

export interface UpdateSkillPayload {
  Name?: string;
  Description?: string;
  Category?: string;
  /** Phase 4.2 / REQ-02: skill grouping Subcategory field. */
  Subcategory?: string | null;
  SortOrder?: number;
  IsActive?: boolean;
}

export interface CreateTaskTypePayload {
  Name: string;
  Description?: string;
  Category?: string;
  /** Phase 4.2 / REQ-02: Subcategory grouping for the task type. */
  Subcategory?: string | null;
  LabSection?: string;
  PresetDurationDays?: number;
  /** Run Mode Phase A: 'attended' (default) | 'continuous' — engine ignores it until Phase B. */
  RunMode?: 'attended' | 'continuous';
  /** Run Mode Phase A: per-test person minutes for continuous types (0..1440 or null). */
  AttendedMinutes?: number | null;
  /** Amendment 6: fallback machine time in minutes — now admin-writable (null clears). */
  DefaultMinutes?: number | null;
  /** Lab Taxonomy 2026 / D8: required skill (null = no skill, gate passes). */
  SkillKey?: string | null;
  DefaultMachineCount?: number;
  SortOrder?: number;
  IsActive?: boolean;
}

export interface UpdateTaskTypePayload {
  Name?: string;
  Description?: string;
  Category?: string;
  /** Phase 4.2 / REQ-02: Subcategory grouping for the task type. */
  Subcategory?: string | null;
  LabSection?: string;
  PresetDurationDays?: number;
  /** Run Mode Phase A: 'attended' (default) | 'continuous' — engine ignores it until Phase B. */
  RunMode?: 'attended' | 'continuous';
  /** Run Mode Phase A: per-test person minutes for continuous types (0..1440 or null). */
  AttendedMinutes?: number | null;
  /** Amendment 6: fallback machine time in minutes — now admin-writable (null clears). */
  DefaultMinutes?: number | null;
  /** Lab Taxonomy 2026 / D8: required skill (null = no skill, gate passes). */
  SkillKey?: string | null;
  DefaultMachineCount?: number;
  SortOrder?: number;
  IsActive?: boolean;
}

export interface CreateTestPrerequisitePayload {
  TestCatalogItemKey: string;
  PrerequisiteItemKey: string;
  MinSkillLevel?: SkillLevel;
  Notes?: string;
}

export interface UpdateTestPrerequisitePayload {
  MinSkillLevel?: SkillLevel | null;
  Notes?: string | null;
}

// ── Read service functions ─────────────────────────────────────────────────────

/**
 * Fetch skills.
 * GET /api/taxonomy/skills
 *
 * Phase 3.5: Pass includeInactive=true (admin only) to also receive inactive
 * skills — the management dialog needs them so admins can reactivate. The
 * backend only honours this flag for admin-role callers; non-admins always
 * get active-only regardless of the query param.
 */
export async function fetchSkills(includeInactive = false): Promise<Skill[]> {
  const params: Record<string, string> = {};
  if (includeInactive) params.includeInactive = 'true';
  const response = await apiClient.get<Skill[]>('/taxonomy/skills', { params });
  return response.data;
}

/**
 * Fetch active task types. Pass a category string to filter.
 * GET /api/taxonomy/task-types?category=...
 */
export async function fetchTaskTypes(category?: string): Promise<TaskType[]> {
  const params: Record<string, string> = {};
  if (category) params.category = category;
  const response = await apiClient.get<TaskType[]>('/taxonomy/task-types', { params });
  return response.data;
}

/**
 * Fetch all active TestCatalogItem → TaskType mapping rows.
 * Phase 2 "Generate Tasks" loads this once to materialise ScheduleTask rows.
 * GET /api/taxonomy/test-catalog-task-types
 */
export async function fetchTestCatalogTaskTypes(): Promise<TestCatalogTaskTypeMapping[]> {
  const response = await apiClient.get<TestCatalogTaskTypeMapping[]>(
    '/taxonomy/test-catalog-task-types',
  );
  return response.data;
}

/**
 * Fetch active test prerequisites with joined names.
 * Pass testCatalogItemKey to narrow to a single test's prerequisites.
 * Phase 5 admin UI uses the filter; the quote chips pre-load all.
 * GET /api/taxonomy/test-prerequisites[?testCatalogItemKey=...]
 */
export async function fetchTestPrerequisites(
  testCatalogItemKey?: string,
): Promise<TestPrerequisiteDetail[]> {
  const params: Record<string, string> = {};
  if (testCatalogItemKey) params.testCatalogItemKey = testCatalogItemKey;
  const response = await apiClient.get<TestPrerequisiteDetail[]>(
    '/taxonomy/test-prerequisites',
    { params },
  );
  return response.data;
}

// ── Write service functions — all wired (follow-up 1.2) ──────────────────────

/**
 * Create a new Skill.
 * POST /api/taxonomy/skills
 * Returns 400 on validation error, 409 on duplicate name.
 */
export async function createSkill(data: CreateSkillPayload): Promise<Skill> {
  const response = await apiClient.post<Skill>('/taxonomy/skills', data);
  return response.data;
}

/**
 * Update an existing Skill.
 * PUT /api/taxonomy/skills/:key
 * Returns 400/404/409 on error.
 */
export async function updateSkill(key: string, data: UpdateSkillPayload): Promise<Skill> {
  const response = await apiClient.put<Skill>(`/taxonomy/skills/${key}`, data);
  return response.data;
}

/**
 * Soft-delete a Skill.
 * DELETE /api/taxonomy/skills/:key
 * Returns 204 on success, 404 if not found.
 */
export async function deleteSkill(key: string): Promise<void> {
  await apiClient.delete(`/taxonomy/skills/${key}`);
}

/**
 * Create a new TaskType.
 * POST /api/taxonomy/task-types
 * Returns 400 on validation error, 409 on duplicate name.
 */
export async function createTaskType(data: CreateTaskTypePayload): Promise<TaskType> {
  const response = await apiClient.post<TaskType>('/taxonomy/task-types', data);
  return response.data;
}

/**
 * Update an existing TaskType.
 * PUT /api/taxonomy/task-types/:key
 * Returns 400/404/409 on error.
 */
export async function updateTaskType(key: string, data: UpdateTaskTypePayload): Promise<TaskType> {
  const response = await apiClient.put<TaskType>(`/taxonomy/task-types/${key}`, data);
  return response.data;
}

/**
 * Soft-delete a TaskType.
 * DELETE /api/taxonomy/task-types/:key
 * Returns 204. Returns 409 if referenced by an active MachineTaskCapability.
 */
export async function deleteTaskType(key: string): Promise<void> {
  await apiClient.delete(`/taxonomy/task-types/${key}`);
}

/**
 * Create a new TestPrerequisite.
 * POST /api/taxonomy/test-prerequisites
 * Returns 400 for self-reference or circular dependency.
 */
export async function createTestPrerequisite(
  data: CreateTestPrerequisitePayload,
): Promise<TestPrerequisiteDetail> {
  const response = await apiClient.post<TestPrerequisiteDetail>(
    '/taxonomy/test-prerequisites',
    data,
  );
  return response.data;
}

/**
 * Update a TestPrerequisite's MinSkillLevel and/or Notes.
 * PUT /api/taxonomy/test-prerequisites/:key
 */
export async function updateTestPrerequisite(
  key: string,
  data: UpdateTestPrerequisitePayload,
): Promise<TestPrerequisiteDetail> {
  const response = await apiClient.put<TestPrerequisiteDetail>(
    `/taxonomy/test-prerequisites/${key}`,
    data,
  );
  return response.data;
}

/**
 * Soft-delete a TestPrerequisite (sets sysLastAction='delete').
 * DELETE /api/taxonomy/test-prerequisites/:key
 */
export async function deleteTestPrerequisite(key: string): Promise<void> {
  await apiClient.delete(`/taxonomy/test-prerequisites/${key}`);
}

// ── TaskType ↔ Machine capability (Phase 5.6 — test-side "Machines & Time" editor) ──

/** Capability row returned by GET /api/taxonomy/task-types/:key/capabilities. */
export interface TaskTypeCapabilityRow {
  MachineTaskCapabilityKey: string;
  MachineKey: string;
  MachineCode: string;
  MachineName: string;
  LabSection: string | null;
  MachineStatus: string;
  /** Machine time in minutes for this test×machine pairing. Null = unmapped. */
  MachineMinutes: number | null;
  /** REQ-06: this machine is the TaskType's default for auto-pick. At most one per TaskType. */
  IsDefault: boolean;
  Notes: string | null;
}

/** Input shape for setting capabilities from the test side. */
export interface TaskTypeCapabilityInput {
  MachineKey: string;
  /** Machine time in minutes. Null = unmapped. */
  MachineMinutes?: number | null;
  /** REQ-06: mark this machine as the TaskType's default for auto-pick. */
  IsDefault?: boolean;
}

/**
 * Fetch active MachineTaskCapability rows for a TaskType with joined Machine details.
 * GET /api/taxonomy/task-types/:key/capabilities
 */
export async function fetchTaskTypeCapabilities(
  taskTypeKey: string,
): Promise<TaskTypeCapabilityRow[]> {
  const response = await apiClient.get<TaskTypeCapabilityRow[]>(
    `/taxonomy/task-types/${taskTypeKey}/capabilities`,
  );
  return response.data;
}

/**
 * Set the full machine-capability list for a TaskType (set-replace semantics).
 * PUT /api/taxonomy/task-types/:key/capabilities
 * Body: [{ MachineKey, MachineMinutes? }]
 * Returns the refreshed list.
 */
export async function setTaskTypeCapabilities(
  taskTypeKey: string,
  capabilities: TaskTypeCapabilityInput[],
): Promise<TaskTypeCapabilityRow[]> {
  const response = await apiClient.put<TaskTypeCapabilityRow[]>(
    `/taxonomy/task-types/${taskTypeKey}/capabilities`,
    capabilities,
  );
  return response.data;
}
